import { ArenaInterface } from '@/models/setup/Arena'
import { getCountryForTimezone } from 'countries-and-timezones'
import Vue from 'vue'
import Vuex, { Store } from 'vuex'
import cartModule from './modules/cart'
import { CartState } from './modules/cart'
import { GlobalProductInterface } from '@/models/shop/GlobalProduct.types'

function getCountryFromTimezone(fallback = 'us'): string {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
  return getCountryForTimezone(tz)?.id?.toLowerCase() ?? fallback
}

// Re-export cart types for convenience
export type {
  CartItem,
  CartState,
  UpdateVariantQuantityPayload,
  UpdatePromotionQuantityPayload,
} from './modules/cart'

export interface SnackbarState {
  show: boolean
  color: string
  message: string
  timeout: number
  icon: string
}

export interface RootState {
  authenticated: boolean
  arena: ArenaInterface | null
  country: string
  snackbar: SnackbarState
  cart: CartState
  selectedGlobalProduct: GlobalProductInterface | null
}

export interface ShowSnackbarPayload {
  message: string
  show?: boolean
  color?: string
  timeout?: number
  icon?: string
}

Vue.use(Vuex)

// Fresh copy of the root state. Used both to seed the store and to restore it
// on logout, so no root-level state (auth, arena, country, selected global
// product, etc.) leaks into the next session.
function getDefaultRootState(): RootState {
  return {
    authenticated: false,
    arena: null,
    country: getCountryFromTimezone(),
    snackbar: {
      show: false,
      color: '',
      message: '',
      timeout: 3000,
      icon: 'check_circle',
    },
    selectedGlobalProduct: null,
  } as RootState
}

export default new Store<RootState>({
  state: getDefaultRootState(),
  mutations: {
    // Restore root state to its initial values. The `cart` module is reset
    // separately via `cart/resetCartState` (this does not touch module state).
    resetState(state: RootState): void {
      Object.assign(state, getDefaultRootState())
    },
    setAuthenticated(state: RootState, authenticated: boolean): void {
      state.authenticated = authenticated
    },
    setArena(state: RootState, arena: ArenaInterface | null): void {
      state.arena = arena
    },
    setCountry(state: RootState, country: string): void {
      state.country = country
    },
    SHOW_SNACKBAR(state: RootState, snackbar: SnackbarState): void {
      state.snackbar = snackbar
    },
    HIDE_SNACKBAR(state: RootState): void {
      state.snackbar.show = false
    },
    setSelectedGlobalProduct(
      state: RootState,
      product: GlobalProductInterface | null,
    ): void {
      state.selectedGlobalProduct = product
    },
  },
  getters: {
    authenticated: (state: RootState): boolean => {
      return state.authenticated
    },
    arena: (state: RootState): ArenaInterface | null => {
      return state.arena
    },
    country: (state: RootState): string => {
      return state.country
    },
    snackbar: (state: RootState): SnackbarState => state.snackbar,
    selectedGlobalProduct: (state: RootState): GlobalProductInterface | null =>
      state.selectedGlobalProduct,
  },
  actions: {
    showSnackbar(
      {
        commit,
      }: { commit: (mutation: string, payload?: ShowSnackbarPayload) => void },
      payload: ShowSnackbarPayload,
    ): void {
      const {
        message,
        show = true,
        color = 'success',
        timeout = 3000,
        icon = 'check_circle',
      } = payload
      commit('SHOW_SNACKBAR', { show, color, message, timeout, icon })
    },
    hideSnackbar({ commit }: { commit: (mutation: string) => void }): void {
      commit('HIDE_SNACKBAR')
    },
  },
  modules: {
    cart: cartModule,
  },
})
